
import MBaseModal from "@/components/MBaseModal.vue";
import MIcon from "@/components/MIcon.vue";
import MButton from "@/components/MButton.vue";
import MTextField from "@/components/form/MTextField.vue";
import MSelectBox from "@/components/form/MSelectBox.vue";
import MscChangeEmailModal from "@/components/school/MscChangeEmailModal.vue";
import store from "@/store";
import { Options, Vue } from "vue-class-component";
import {
  getDefaultSchoolAiChatMessageLimitConfig,
  Role,
  School,
  SchoolAiResourceLimitConfig,
  SchoolAiUsageLimits,
  SchoolConfig,
  SubscriptionType,
  Prefecture,
  PREFECTURES,
  SchoolCategory,
  SCHOOL_CATEGORY_LABELS,
  FoundingBody,
  FOUNDING_BODY_LABELS,
  UserContractScope,
  USER_CONTRACT_SCOPE_LABELS
} from "@/entities/school";
import { labelsToSelectOptions } from "@/utils/school";
import { Target } from "@/entities/target";
import dayjs from "dayjs";
import {
  updateSchool,
  updateSchoolConfig,
  updateSchoolRecess,
  reflectUpdatedRecessToChildren
} from "@/api/school";
import { updateStudentRecess } from "@/api/student";
import { convertToUnixtimeFromDate } from "@/utils/date";
import { saveErrorLog } from "@/api/error";
import { User } from "@/entities/user";
import { changeUserEmail } from "@/api/user";

type PaymentType = {
  key: "uncontracted" | "stripe" | "external" | "none";
  name: string;
};

type UsageLimitPeriodOption = {
  value: "daily" | "weekly" | "monthly" | "yearly" | "all";
  label: string;
};

function convertToEndOfDayUnixtime(date: string): number | null {
  return date ? dayjs(date).endOf("day").unix() : null;
}

@Options({
  components: {
    MBaseModal,
    MIcon,
    MButton,
    MTextField,
    MSelectBox,
    MscChangeEmailModal
  },
  emits: ["close"],
  props: {
    school: Object,
    schoolConfig: Object,
    schools: Array,
    targets: Array,
    tutors: Array,
    isSuperAdmin: Boolean,
    isServiceProvider: Boolean
  },
  watch: {
    addRole(newValue) {
      if (newValue === false) {
        this.role = [];
      }
    }
  }
})
export default class MscEditModal extends Vue {
  isSuperAdmin = false;
  isServiceProvider = false;
  school!: School;
  schoolConfig!: SchoolConfig;
  schoolId = "";
  schoolName = "";
  paymentType: "uncontracted" | "stripe" | "external" | "none" = "none";
  paymentTypes: PaymentType[] = [
    {
      key: "stripe",
      name: "クレジットカード (Stripe)"
    },
    {
      key: "external",
      name: "外部決済 (銀行振込等)"
    },
    {
      key: "uncontracted",
      name: "利用停止中 (未契約)"
    },
    {
      key: "none",
      name: "デモ・トライアル期間"
    }
  ];
  showToast = false;
  isFreeSubscription = false;
  isPaidSubscription = false;
  isOtherSubscription = false;
  prefecture: Prefecture | null = null;
  schoolCategory: SchoolCategory | null = null;
  foundingBody: FoundingBody | null = null;
  userContractScope: UserContractScope | null = null;
  schoolAiUsageExpiresAtDate = "";
  freeSubscriptionStartDay = "";
  freeSubscriptionEndDay = "";
  paidSubscriptionStartDay = "";
  paidSubscriptionEndDay = "";
  allowCreateRoom = false;
  allowUseOfCustomOfficialLine = false;
  activateDay = "";
  inRecess = false;
  selectedTargetId = "";
  schools: School[] = [];
  targets: Target[] = [];
  zoomUserId = "";
  lineOfficialAccountChannelAccessToken = "";
  lineOfficialAccountChannelSecret = "";
  role: Role[] = [];
  tutors: User[] = [];
  addRole = false;
  isEditedSchoolServiceProvider = false;
  useSchoolAiExclusively = false;
  useStudyRoomOnly = false;
  hideLoginInvitaion = false;
  hideMingakuStudyRoom = false;
  allowStudentsToChangePassword = false;
  isPromptCreationForbidden = false;
  disableStudentsPromptCreation = false;
  canUseLearningCard = false;
  canUseDigitalDrill = false;
  canUsePrintSubmission = false;
  canSaveVoiceInputAudioInSchoolAiChat = false;
  canUsePiP = false;
  canShowKawasemiLight = false;
  canUsePronunciationAssessment = false;
  canOwnerViewOtherStaffChats = false;
  canShowTemplatesAndGuidesInSchoolAi = false;
  canPublishAppToStore = false;
  contextCompressionCanaryEnabled = false;
  showChangeEmailModal = false;
  selectedTutorEmail = "";
  selectedTutor: User | null = null;
  maxTutorCount: number | null = null;
  maxStudentCount: number | null = null;
  usageLimitPeriods: UsageLimitPeriodOption[] = [
    {
      value: "daily",
      label: "daily / 日次"
    },
    {
      value: "weekly",
      label: "weekly / 週次（月曜始まり）"
    },
    {
      value: "monthly",
      label: "monthly / 月次"
    },
    {
      value: "yearly",
      label: "yearly / 年次"
    },
    {
      value: "all",
      label: "all / 累計"
    }
  ];
  studentChatMessageUsageLimitConfig: SchoolAiResourceLimitConfig = getDefaultSchoolAiChatMessageLimitConfig();
  allowStudentIndividualChatMessageSettings = true;
  studentChatMessageLimitExpiresAtDate = "";
  tutorChatMessageUsageLimitConfig: SchoolAiResourceLimitConfig = getDefaultSchoolAiChatMessageLimitConfig();
  tutorChatMessageLimitExpiresAtDate = "";
  schoolLevelChatMessageUsageLimitConfig: SchoolAiResourceLimitConfig = getDefaultSchoolAiChatMessageLimitConfig();
  schoolLevelChatMessageLimitExpiresAtDate = "";
  usageLimitStudentOpen = false;
  usageLimitTutorOpen = false;
  usageLimitSchoolLevelOpen = false;

  get schoolCategoryOptions() {
    return labelsToSelectOptions(SCHOOL_CATEGORY_LABELS);
  }

  get foundingBodyOptions() {
    return labelsToSelectOptions(FOUNDING_BODY_LABELS);
  }

  get userContractScopeOptions() {
    return labelsToSelectOptions(USER_CONTRACT_SCOPE_LABELS);
  }

  get PREFECTURES() {
    return PREFECTURES;
  }

  get isSchool(): boolean {
    return store.state.isSchool;
  }

  get selectedTarget(): Target | null {
    const matchTargets = this.targets.filter(
      target => target.ref.id === this.selectedTargetId
    );
    if (matchTargets.length === 0) {
      return null;
    }
    return matchTargets[0];
  }

  get isSelectedTargetSchool(): boolean {
    return this.selectedTarget?.data.key === "school";
  }

  get studentListLink(): string {
    return `/student?school=${this.school.ref.id}`;
  }

  get validId(): boolean {
    if (this.schoolId === this.school.data.id) {
      return true;
    }

    return (
      /^\d{6}$/.test(this.schoolId) &&
      !this.schools.map(school => school.data.id).includes(this.schoolId)
    );
  }

  get validName(): boolean {
    return this.schoolName.length > 0;
  }

  get validActivateDay(): boolean {
    if (this.schoolConfig.data.activateTime) {
      return true;
    }
    if (this.activateDay === "") {
      return true;
    }

    return /^\d{4}-\d{2}-\d{2}$/.test(this.activateDay);
  }

  get validStudentChatMessageUsageLimit(): boolean {
    if (
      !this.isSuperAdmin ||
      !this.studentChatMessageUsageLimitConfig.enabled
    ) {
      return true;
    }

    const { limit } = this.studentChatMessageUsageLimitConfig;
    return typeof limit === "number" && Number.isInteger(limit) && limit >= 1;
  }

  get validStudentChatMessageWarningThreshold(): boolean {
    return this.isValidChatMessageWarningThreshold(
      this.studentChatMessageUsageLimitConfig
    );
  }

  get validTutorChatMessageUsageLimit(): boolean {
    if (!this.isSuperAdmin || !this.tutorChatMessageUsageLimitConfig.enabled) {
      return true;
    }

    const { limit } = this.tutorChatMessageUsageLimitConfig;
    return typeof limit === "number" && Number.isInteger(limit) && limit >= 1;
  }

  get validTutorChatMessageWarningThreshold(): boolean {
    return this.isValidChatMessageWarningThreshold(
      this.tutorChatMessageUsageLimitConfig
    );
  }

  get validSchoolLevelChatMessageUsageLimit(): boolean {
    if (
      !this.isSuperAdmin ||
      !this.schoolLevelChatMessageUsageLimitConfig.enabled
    ) {
      return true;
    }

    const { limit } = this.schoolLevelChatMessageUsageLimitConfig;
    return typeof limit === "number" && Number.isInteger(limit) && limit >= 1;
  }

  get validSchoolLevelChatMessageWarningThreshold(): boolean {
    return this.isValidChatMessageWarningThreshold(
      this.schoolLevelChatMessageUsageLimitConfig
    );
  }

  get validData() {
    return (
      this.validId &&
      this.validName &&
      this.validActivateDay &&
      this.validStudentChatMessageUsageLimit &&
      this.validStudentChatMessageWarningThreshold &&
      this.validTutorChatMessageUsageLimit &&
      this.validTutorChatMessageWarningThreshold &&
      this.validSchoolLevelChatMessageUsageLimit &&
      this.validSchoolLevelChatMessageWarningThreshold
    );
  }

  get schoolAiUsageLimitsForUpdate(): SchoolAiUsageLimits | undefined {
    if (!this.isSuperAdmin) {
      return undefined;
    }

    return {
      student: {
        allowStudentIndividualChatMessageSettings: this
          .allowStudentIndividualChatMessageSettings,
        chatMessage: this.buildSchoolChatMessageUsageLimitForUpdate(
          this.studentChatMessageUsageLimitConfig,
          this.studentChatMessageLimitExpiresAtDate
        )
      },
      tutor: {
        chatMessage: this.buildSchoolChatMessageUsageLimitForUpdate(
          this.tutorChatMessageUsageLimitConfig,
          this.tutorChatMessageLimitExpiresAtDate
        )
      },
      schoolLevel: {
        chatMessage: this.buildSchoolChatMessageUsageLimitForUpdate(
          this.schoolLevelChatMessageUsageLimitConfig,
          this.schoolLevelChatMessageLimitExpiresAtDate
        )
      }
    };
  }

  buildSchoolChatMessageUsageLimitForUpdate(
    config: SchoolAiResourceLimitConfig,
    expiresAtDate: string
  ): SchoolAiResourceLimitConfig {
    return {
      ...config,
      limit: typeof config.limit === "number" ? Math.floor(config.limit) : null,
      warningThreshold:
        typeof config.warningThreshold === "number"
          ? Math.floor(config.warningThreshold)
          : null,
      countSystemPrompt: config.countSystemPrompt ?? false,
      expiresAt: expiresAtDate
        ? dayjs(expiresAtDate)
            .endOf("day")
            .unix()
        : null
    };
  }

  isValidChatMessageWarningThreshold(
    config: SchoolAiResourceLimitConfig
  ): boolean {
    if (!this.isSuperAdmin || !config.enabled) {
      return true;
    }

    if (
      config.warningThreshold === null ||
      typeof config.warningThreshold === "undefined"
    ) {
      return true;
    }

    if (
      !(
        typeof config.warningThreshold === "number" &&
        Number.isInteger(config.warningThreshold) &&
        config.warningThreshold >= 0
      )
    ) {
      return false;
    }

    return (
      typeof config.limit === "number" &&
      Number.isInteger(config.limit) &&
      config.limit >= 1 &&
      config.warningThreshold <= config.limit
    );
  }

  async changeRecess() {
    store.commit("START_LOADING", "更新中...");
    try {
      const recessTime = this.schoolConfig.data.recessTime
        ? 0 // 休会解除
        : Math.floor(Date.now() / 1000); // 休会
      const classroomsSnapshot = await this.school.ref
        .collection("classrooms")
        .get();
      await Promise.all(
        classroomsSnapshot.docs.map(async doc => {
          const studentsSnapshot = await doc.ref.collection("students").get();
          studentsSnapshot.docs.map(async doc => {
            if (
              doc.data().recessTime !== 0 &&
              typeof doc.data().recessTime !== "undefined"
            )
              return; // すでに生徒が休会状態だったら、recessTime を更新しない
            await updateStudentRecess(doc.ref, recessTime); // 生徒の休会 or 休会解除
          });
        })
      );
      await Promise.all([
        updateSchoolRecess(this.schoolConfig.ref.id, recessTime),
        reflectUpdatedRecessToChildren(
          this.isEditedSchoolServiceProvider,
          this.schoolConfig.ref,
          recessTime
        )
      ]);
      store.commit("END_LOADING");
    } catch (e) {
      store.commit("END_LOADING");
      alert(e);
      await saveErrorLog(
        store.state.role,
        e.code,
        e.message,
        "Failed to change recess status"
      );
      return;
    }
    this.$router.go(0);
  }

  handleMaxTutorCountInput(value: string) {
    if (!value) {
      this.maxTutorCount = null;
      return;
    }
    const num = Number(value);
    // NaN（数字でない）または マイナス値のチェック
    if (isNaN(num) || num < 0) {
      if (isNaN(num)) {
        alert("先生アカウントの最大数には数字を入力してください");
      } else {
        alert("先生アカウントの最大数にマイナスの値は設定できません");
      }
      // 強制的に再レンダリングさせるため、一度nullにしてから0に設定
      this.maxTutorCount = null;
      this.$nextTick(() => {
        this.maxTutorCount = 0;
      });
      return;
    }
    this.maxTutorCount = num;
  }

  handleMaxStudentCountInput(value: string) {
    if (!value) {
      this.maxStudentCount = null;
      return;
    }
    const num = Number(value);
    // NaN（数字でない）または マイナス値のチェック
    if (isNaN(num) || num < 0) {
      if (isNaN(num)) {
        alert("生徒アカウントの最大数には数字を入力してください");
      } else {
        alert("生徒アカウントの最大数にマイナスの値は設定できません");
      }
      // 強制的に再レンダリングさせるため、一度nullにしてから0に設定
      this.maxStudentCount = null;
      this.$nextTick(() => {
        this.maxStudentCount = 0;
      });
      return;
    }
    this.maxStudentCount = num;
  }

  validateMaxTutorCount() {
    if (this.maxTutorCount !== null && this.maxTutorCount < 0) {
      alert("先生アカウントの最大数にマイナスの値は設定できません");
      this.maxTutorCount = 0;
    }
  }

  validateMaxStudentCount() {
    if (this.maxStudentCount !== null && this.maxStudentCount < 0) {
      alert("生徒アカウントの最大数にマイナスの値は設定できません");
      this.maxStudentCount = 0;
    }
  }

  handleStudentChatMessageLimitInput(value: string) {
    if (!value) {
      this.studentChatMessageUsageLimitConfig.limit = null;
      return;
    }

    const limit = Number(value);
    this.studentChatMessageUsageLimitConfig.limit = Number.isFinite(limit)
      ? Math.floor(limit)
      : null;
  }

  handleStudentChatMessageWarningThresholdInput(value: string) {
    if (!value) {
      this.studentChatMessageUsageLimitConfig.warningThreshold = null;
      return;
    }

    const warningThreshold = Number(value);
    this.studentChatMessageUsageLimitConfig.warningThreshold = Number.isFinite(
      warningThreshold
    )
      ? Math.floor(warningThreshold)
      : null;
  }

  handleTutorChatMessageLimitInput(value: string) {
    if (!value) {
      this.tutorChatMessageUsageLimitConfig.limit = null;
      return;
    }

    const limit = Number(value);
    this.tutorChatMessageUsageLimitConfig.limit = Number.isFinite(limit)
      ? Math.floor(limit)
      : null;
  }

  handleTutorChatMessageWarningThresholdInput(value: string) {
    if (!value) {
      this.tutorChatMessageUsageLimitConfig.warningThreshold = null;
      return;
    }

    const warningThreshold = Number(value);
    this.tutorChatMessageUsageLimitConfig.warningThreshold = Number.isFinite(
      warningThreshold
    )
      ? Math.floor(warningThreshold)
      : null;
  }

  handleSchoolLevelChatMessageLimitInput(value: string) {
    if (!value) {
      this.schoolLevelChatMessageUsageLimitConfig.limit = null;
      return;
    }

    const limit = Number(value);
    this.schoolLevelChatMessageUsageLimitConfig.limit = Number.isFinite(limit)
      ? Math.floor(limit)
      : null;
  }

  handleSchoolLevelChatMessageWarningThresholdInput(value: string) {
    if (!value) {
      this.schoolLevelChatMessageUsageLimitConfig.warningThreshold = null;
      return;
    }

    const warningThreshold = Number(value);
    this.schoolLevelChatMessageUsageLimitConfig.warningThreshold = Number.isFinite(
      warningThreshold
    )
      ? Math.floor(warningThreshold)
      : null;
  }

  async update() {
    if (!this.validData || !this.selectedTarget) {
      return;
    }

    if (this.addRole && this.role.length === 0) {
      alert("役割を選択してください");
      return;
    }

    if (
      this.allowUseOfCustomOfficialLine &&
      (!this.lineOfficialAccountChannelAccessToken.trim() ||
        !this.lineOfficialAccountChannelSecret.trim())
    ) {
      alert(
        "独自公式LINEを利用する場合はチャネルアクセストークンとチャネルシークレットを入力してください"
      );
      return;
    }

    const recessTime = this.schoolConfig.data.recessTime ?? 0;
    const activateTime =
      this.activateDay !== "" ? convertToUnixtimeFromDate(this.activateDay) : 0;
    store.commit("START_LOADING", "更新中...");

    try {
      const configUpdate = {
        paymentType: this.paymentType,
        subscriptionType: (this.isPaidSubscription
          ? "paid"
          : this.isFreeSubscription
          ? "free"
          : this.isOtherSubscription
          ? "other"
          : null) as SubscriptionType | null,
        freeSubscriptionStartDate: this.freeSubscriptionStartDay
          ? convertToUnixtimeFromDate(this.freeSubscriptionStartDay)
          : null,
        freeSubscriptionEndDate: this.freeSubscriptionEndDay
          ? convertToUnixtimeFromDate(this.freeSubscriptionEndDay)
          : null,
        paidSubscriptionStartDate: this.paidSubscriptionStartDay
          ? convertToUnixtimeFromDate(this.paidSubscriptionStartDay)
          : null,
        paidSubscriptionEndDate: this.paidSubscriptionEndDay
          ? convertToUnixtimeFromDate(this.paidSubscriptionEndDay)
          : null,
        schoolAiUsageExpiresAt: this.isSuperAdmin
          ? convertToEndOfDayUnixtime(this.schoolAiUsageExpiresAtDate)
          : this.schoolConfig.data.schoolAiUsageExpiresAt ?? null,
        recessTime,
        activateTime,
        allowCreateRoom: this.allowCreateRoom,
        target: this.selectedTarget.ref,
        zoomUserId: this.zoomUserId,
        role: this.role,
        useSchoolAiExclusively: this.useSchoolAiExclusively,
        useStudyRoomOnly: this.useStudyRoomOnly,
        hideLoginInvitaion: this.hideLoginInvitaion,
        hideMingakuStudyRoom: this.hideMingakuStudyRoom,
        allowStudentsToChangePassword: this.allowStudentsToChangePassword,
        allowUseOfCustomOfficialLine: this.allowUseOfCustomOfficialLine,
        lineOfficialAccountChannelAccessToken: this
          .lineOfficialAccountChannelAccessToken,
        lineOfficialAccountChannelSecret: this.lineOfficialAccountChannelSecret,
        isPromptCreationForbidden: this.isPromptCreationForbidden,
        disableStudentsPromptCreation: this.disableStudentsPromptCreation,
        canUseLearningCard: this.canUseLearningCard,
        canUseDigitalDrill: this.canUseDigitalDrill,
        canOwnerViewOtherStaffChats: this.canOwnerViewOtherStaffChats,
        canUsePrintSubmission: this.canUsePrintSubmission,
        canSaveVoiceInputAudioInSchoolAiChat: this
          .canSaveVoiceInputAudioInSchoolAiChat,
        canUsePiP: this.canUsePiP,
        canShowKawasemiLight: this.canShowKawasemiLight,
        canUsePronunciationAssessment: this.canUsePronunciationAssessment,
        canShowTemplatesAndGuidesInSchoolAi: this
          .canShowTemplatesAndGuidesInSchoolAi,
        canPublishAppToStore: this.canPublishAppToStore,
        contextCompressionCanaryEnabled: this.contextCompressionCanaryEnabled,
        maxTutorCount: this.maxTutorCount,
        maxStudentCount: this.maxStudentCount,
        prefecture: this.prefecture ?? null,
        schoolCategory: this.schoolCategory ?? null,
        foundingBody: this.foundingBody ?? null,
        userContractScope: this.userContractScope ?? null
      };

      await Promise.all([
        updateSchool(this.school.ref, {
          id: this.schoolId,
          name: this.schoolName,
          schoolAiUsageLimits: this.schoolAiUsageLimitsForUpdate
        }),
        updateSchoolConfig(this.schoolConfig.ref, configUpdate)
      ]);
      store.commit("END_LOADING");
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    } catch (e) {
      store.commit("END_LOADING");
      alert(e);
      await saveErrorLog(
        store.state.role,
        e.code,
        e.message,
        "Failed to update school"
      );
      return;
    }
  }

  onSubscriptionCheck(type: "free" | "paid" | "other") {
    if (type === "free") {
      this.isFreeSubscription = !this.isFreeSubscription;
      if (this.isFreeSubscription) {
        this.isPaidSubscription = false;
        this.isOtherSubscription = false;
      }
    } else if (type === "paid") {
      this.isPaidSubscription = !this.isPaidSubscription;
      if (this.isPaidSubscription) {
        this.isFreeSubscription = false;
        this.isOtherSubscription = false;
      }
    } else {
      this.isOtherSubscription = !this.isOtherSubscription;
      if (this.isOtherSubscription) {
        this.isFreeSubscription = false;
        this.isPaidSubscription = false;
      }
    }
  }

  close() {
    this.$emit("close");
  }

  handleChangeEmail(tutor: User) {
    this.selectedTutor = tutor;
    this.selectedTutorEmail = tutor.email || "";
    this.showChangeEmailModal = true;
  }

  closeChangeEmailModal() {
    this.showChangeEmailModal = false;
    this.selectedTutorEmail = "";
    this.selectedTutor = null;
  }

  async confirmChangeEmail(newEmail: string) {
    if (!this.selectedTutor) {
      return;
    }

    const uid = this.selectedTutor.uid;
    if (!uid) {
      alert("ユーザーIDが見つかりません");
      return;
    }

    store.commit("START_LOADING", "メールアドレスを変更中...");
    try {
      const result = await changeUserEmail(uid, newEmail);

      alert(
        `メールアドレスを ${this.selectedTutorEmail} から ${newEmail} に変更しました`
      );

      this.closeChangeEmailModal();
      store.commit("END_LOADING");
      this.$router.go(0);
    } catch (e) {
      store.commit("END_LOADING");
      alert(`メールアドレスの変更に失敗しました: ${(e as any).message}`);
      await saveErrorLog(
        store.state.role,
        (e as any).code,
        (e as any).message,
        "Failed to change email"
      );
    }
  }

  created() {
    this.schoolId = this.school.data.id;
    this.schoolName = this.school.data.name;
    this.paymentType = this.schoolConfig.data.paymentType;
    const st = this.schoolConfig.data.subscriptionType;
    this.isFreeSubscription = st === "free";
    this.isPaidSubscription = st === "paid";
    this.isOtherSubscription = st === "other";
    if (this.schoolConfig.data.freeSubscriptionStartDate) {
      this.freeSubscriptionStartDay = dayjs(
        this.schoolConfig.data.freeSubscriptionStartDate * 1000
      )
        .locale("ja")
        .format("YYYY-MM-DD");
    }
    if (this.schoolConfig.data.freeSubscriptionEndDate) {
      this.freeSubscriptionEndDay = dayjs(
        this.schoolConfig.data.freeSubscriptionEndDate * 1000
      )
        .locale("ja")
        .format("YYYY-MM-DD");
    }
    if (this.schoolConfig.data.paidSubscriptionStartDate) {
      this.paidSubscriptionStartDay = dayjs(
        this.schoolConfig.data.paidSubscriptionStartDate * 1000
      )
        .locale("ja")
        .format("YYYY-MM-DD");
    }
    if (this.schoolConfig.data.paidSubscriptionEndDate) {
      this.paidSubscriptionEndDay = dayjs(
        this.schoolConfig.data.paidSubscriptionEndDate * 1000
      )
        .locale("ja")
        .format("YYYY-MM-DD");
    }
    if (this.schoolConfig.data.schoolAiUsageExpiresAt) {
      this.schoolAiUsageExpiresAtDate = dayjs(
        this.schoolConfig.data.schoolAiUsageExpiresAt * 1000
      )
        .locale("ja")
        .format("YYYY-MM-DD");
    }
    if (this.schoolConfig.data.recessTime) {
      this.inRecess = true;
    }
    if (this.schoolConfig.data.activateTime) {
      this.activateDay = dayjs(this.schoolConfig.data.activateTime * 1000)
        .locale("ja")
        .format("YYYY-MM-DD");
    }
    this.selectedTargetId = this.schoolConfig.data.target.id;
    this.allowCreateRoom = this.schoolConfig.data.allowCreateRoom ?? false;
    this.zoomUserId = this.schoolConfig.data.zoomUserId ?? "";
    this.allowUseOfCustomOfficialLine =
      this.schoolConfig.data.allowUseOfCustomOfficialLine ?? false;
    this.lineOfficialAccountChannelAccessToken =
      this.schoolConfig.data.lineOfficialAccountChannelAccessToken ?? "";
    this.lineOfficialAccountChannelSecret =
      this.schoolConfig.data.lineOfficialAccountChannelSecret ?? "";

    this.role = this.schoolConfig.data.role
      ? [...this.schoolConfig.data.role]
      : [];
    this.addRole = this.role.length ? true : false;

    this.isEditedSchoolServiceProvider = this.schoolConfig.data.role
      ? this.schoolConfig.data.role.includes("serviceProvider")
      : false;

    this.useSchoolAiExclusively =
      this.schoolConfig.data.useSchoolAiExclusively ?? false;
    this.useStudyRoomOnly = this.schoolConfig.data.useStudyRoomOnly ?? false;
    this.hideLoginInvitaion =
      this.schoolConfig.data.hideLoginInvitaion ?? false;
    this.hideMingakuStudyRoom =
      this.schoolConfig.data.hideMingakuStudyRoom ?? false;
    this.allowStudentsToChangePassword =
      this.schoolConfig.data.allowStudentsToChangePassword ?? false;
    this.isPromptCreationForbidden =
      this.schoolConfig.data.isPromptCreationForbidden ?? false;
    this.disableStudentsPromptCreation =
      this.schoolConfig.data.disableStudentsPromptCreation ?? false;
    this.canUseLearningCard =
      this.schoolConfig.data.canUseLearningCard ?? false;
    this.canUseDigitalDrill =
      this.schoolConfig.data.canUseDigitalDrill ?? false;
    this.canOwnerViewOtherStaffChats =
      this.schoolConfig.data.canOwnerViewOtherStaffChats ?? false;
    this.canUsePrintSubmission =
      this.schoolConfig.data.canUsePrintSubmission ?? false;
    this.canSaveVoiceInputAudioInSchoolAiChat =
      this.schoolConfig.data.canSaveVoiceInputAudioInSchoolAiChat ?? false;
    this.canUsePiP = this.schoolConfig.data.canUsePiP ?? false;
    this.canShowKawasemiLight =
      this.schoolConfig.data.canShowKawasemiLight ?? false;
    this.canUsePronunciationAssessment =
      this.schoolConfig.data.canUsePronunciationAssessment ?? false;
    this.canShowTemplatesAndGuidesInSchoolAi =
      this.schoolConfig.data.canShowTemplatesAndGuidesInSchoolAi ?? false;
    this.canPublishAppToStore =
      this.schoolConfig.data.canPublishAppToStore ?? false;
    this.contextCompressionCanaryEnabled =
      this.schoolConfig.data.contextCompressionCanaryEnabled ?? false;
    this.maxTutorCount = this.schoolConfig.data.maxTutorCount ?? null;
    this.maxStudentCount = this.schoolConfig.data.maxStudentCount ?? null;
    this.prefecture = this.schoolConfig.data.prefecture ?? null;
    this.schoolCategory = this.schoolConfig.data.schoolCategory ?? null;
    this.foundingBody = this.schoolConfig.data.foundingBody ?? null;
    this.userContractScope = this.schoolConfig.data.userContractScope ?? null;

    const studentChatMessageUsageLimit = this.school.data.schoolAiUsageLimits
      ?.student?.chatMessage;
    this.allowStudentIndividualChatMessageSettings =
      this.school.data.schoolAiUsageLimits?.student
        ?.allowStudentIndividualChatMessageSettings ?? true;
    this.studentChatMessageUsageLimitConfig = studentChatMessageUsageLimit
      ? {
          ...getDefaultSchoolAiChatMessageLimitConfig(),
          ...studentChatMessageUsageLimit
        }
      : getDefaultSchoolAiChatMessageLimitConfig();
    this.studentChatMessageLimitExpiresAtDate = studentChatMessageUsageLimit?.expiresAt
      ? dayjs(studentChatMessageUsageLimit.expiresAt * 1000)
          .locale("ja")
          .format("YYYY-MM-DD")
      : "";

    const tutorChatMessageUsageLimit = this.school.data.schoolAiUsageLimits
      ?.tutor?.chatMessage;
    this.tutorChatMessageUsageLimitConfig = tutorChatMessageUsageLimit
      ? {
          ...getDefaultSchoolAiChatMessageLimitConfig(),
          ...tutorChatMessageUsageLimit
        }
      : getDefaultSchoolAiChatMessageLimitConfig();
    this.tutorChatMessageLimitExpiresAtDate = tutorChatMessageUsageLimit?.expiresAt
      ? dayjs(tutorChatMessageUsageLimit.expiresAt * 1000)
          .locale("ja")
          .format("YYYY-MM-DD")
      : "";

    const schoolLevelChatMessageUsageLimit = this.school.data
      .schoolAiUsageLimits?.schoolLevel?.chatMessage;
    this.schoolLevelChatMessageUsageLimitConfig = schoolLevelChatMessageUsageLimit
      ? {
          ...getDefaultSchoolAiChatMessageLimitConfig(),
          ...schoolLevelChatMessageUsageLimit
        }
      : getDefaultSchoolAiChatMessageLimitConfig();
    this.schoolLevelChatMessageLimitExpiresAtDate = schoolLevelChatMessageUsageLimit?.expiresAt
      ? dayjs(schoolLevelChatMessageUsageLimit.expiresAt * 1000)
          .locale("ja")
          .format("YYYY-MM-DD")
      : "";
  }
}
